import { z } from "zod/v4"
import { VoiceEmoteId, VoiceEmoteIdSchema } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { UserDenormalizedWithoutLook, Users } from "./user"

// ─── VoiceEmoteDocument ──────────────────────────────────────────────────────
// Path: users/{userId}/voices/{voiceId}/voice-emotes/{voiceEmoteId}

export const VoiceEmoteKind = z.enum(["thumbsup", "surprised", "laughter", "clapping"])
  .describe("エモートの種類")

export type VoiceEmoteKind = z.infer<typeof VoiceEmoteKind>

export const VoiceEmoteTiming = z.enum(["playing", "ending"])
  .describe("エモートが送られたタイミング。playing=再生中、ending=再生終了後")

export type VoiceEmoteTiming = z.infer<typeof VoiceEmoteTiming>

export const VoiceEmoteDocumentMeta = z.object({
  user: UserDenormalizedWithoutLook.describe("エモート送信者の非正規化ユーザー情報"),
  kind: VoiceEmoteKind,
  timing: VoiceEmoteTiming,
})

export type VoiceEmoteDocumentMeta = z.infer<typeof VoiceEmoteDocumentMeta>

export const VoiceEmoteDocumentBody = z.intersection(
  VoiceEmoteDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type VoiceEmoteDocumentBody = z.infer<typeof VoiceEmoteDocumentBody>

export const VoiceEmoteDocument = z.intersection(
  VoiceEmoteDocumentBody,
  z.object({
    id: VoiceEmoteIdSchema,
  })
)

export type VoiceEmoteDocument = z.infer<typeof VoiceEmoteDocument>

export class VoiceEmoteDocuments {

  static toDocumentData(emote: VoiceEmoteDocument): object {
    return {
      ...emote,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): VoiceEmoteDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: VoiceEmoteId(doc.id),
      user: Users.parseDenormalizedWithoutLook(data["user"]),
      kind: data["kind"],
      timing: data["timing"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }

}

// ─── API Request/Response ────────────────────────────────────────────────────

export const CreateVoiceEmoteRequest = z.object({
  kind: VoiceEmoteKind,
  timing: VoiceEmoteTiming,
})
export type CreateVoiceEmoteRequest = z.infer<typeof CreateVoiceEmoteRequest>

export const CreateVoiceEmoteResponse = z.object({
  id: z.string().describe("作成されたエモートのID"),
})
export type CreateVoiceEmoteResponse = z.infer<typeof CreateVoiceEmoteResponse>
